/** @typedef {{ firstName?: string; email?: string; source?: string }} VoiceNoteSubmissionContext */

const NAME_MAX = 200;
const EMAIL_MAX = 320;
const SOURCE_MAX = 120;

/**
 * Reads guest context from the voicenote URL for Firestore on submit.
 * - Query: `/voicenote?first_name=Jane&email=jane@example.com&source=faq_registry`
 * - Hash: `/voicenote#first_name=Jane&email=jane@example.com`
 * - Also tolerates repeated `#` (e.g. `#first_name=x#email=y`) by normalising to `&`.
 * Aliases: `firstName` alongside `first_name`; attribution `source` alongside `ref` / `utm_source`.
 *
 * @returns {VoiceNoteSubmissionContext}
 */
export function readVoiceNoteSubmissionContext() {
  if (typeof window === 'undefined') return {};

  const fromSearch = new URLSearchParams(window.location.search);

  let hashRaw = window.location.hash.replace(/^#/, '');
  /** @type {URLSearchParams} */
  let fromHash = new URLSearchParams();
  if (hashRaw) {
    const normalized = hashRaw.includes('&') ? hashRaw : hashRaw.replace(/#/g, '&');
    fromHash = new URLSearchParams(normalized);
  }

  const pick = (/** @type {string[]} */ keys) => {
    for (const k of keys) {
      const v = fromHash.get(k) ?? fromSearch.get(k);
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  let firstName = pick(['first_name', 'firstName']);
  let email = pick(['email']);
  let source = pick(['source', 'ref', 'utm_source']);

  if (firstName.length > NAME_MAX) firstName = firstName.slice(0, NAME_MAX);
  if (email.length > EMAIL_MAX) email = email.slice(0, EMAIL_MAX);
  if (source.length > SOURCE_MAX) source = source.slice(0, SOURCE_MAX);

  /** @type {VoiceNoteSubmissionContext} */
  const out = {};
  if (firstName) out.firstName = firstName;
  if (email) out.email = email;
  if (source) out.source = source;
  return out;
}
